import { useMemo } from 'react';
import { Link, router } from '@inertiajs/react';
import { motion } from 'framer-motion';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';

export default function Show({
    query,
    quotation,
    serviceTypes,
    serviceIcons,
    serviceColors,
    mealPlans,
}) {
    // Group items by day
    const itemsByDay = useMemo(() => {
        const grouped = {};
        (quotation.items || []).forEach(item => {
            const day = item.day_number || 1;
            if (!grouped[day]) grouped[day] = [];
            grouped[day].push(item);
        });
        return grouped;
    }, [quotation.items]);

    const days = Object.keys(itemsByDay).map(Number).sort((a, b) => a - b);

    const currency = quotation.currency || { symbol: '₹' };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return `${currency.symbol}${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const getServiceIcon = (type) => serviceIcons[type] || 'bi-box';
    const getServiceColor = (type) => serviceColors[type] || '#64748b';

    const getStatusColor = (status) => {
        const colors = {
            draft: 'secondary',
            sent: 'info',
            accepted: 'success',
            rejected: 'danger',
            expired: 'warning',
        };
        return colors[status] || 'secondary';
    };

    const handleStatusChange = (status) => {
        router.patch(`/queries/${query.id}/quotations/${quotation.id}/status`, {
            status,
        });
    };

    const totals = useMemo(() => {
        const cost = parseFloat(quotation.total_cost) || 0;
        const selling = parseFloat(quotation.total_selling) || 0;
        const profit = selling - cost;
        const travelers = (quotation.adults || 0) + (quotation.children || 0) + (quotation.infants || 0);
        const perPerson = travelers > 0 ? selling / travelers : 0;

        return { cost, selling, profit, travelers, perPerson };
    }, [quotation]);

    const breadcrumbs = [
        { label: 'Queries', href: '/queries' },
        { label: query.query_number, href: `/queries/${query.id}` },
        { label: 'Quotations', href: `/queries/${query.id}?tab=quotations` },
        { label: quotation.quotation_number },
    ];

    return (
        <AppLayout title={quotation.quotation_number}>
            <PageHeader
                title={quotation.quotation_number}
                subtitle={quotation.title || 'Quotation Preview'}
                breadcrumbs={breadcrumbs}
                actions={
                    <div className="d-flex gap-2">
                        <div className="dropdown">
                            <button
                                className={`btn btn-${getStatusColor(quotation.status)} dropdown-toggle`}
                                type="button"
                                data-bs-toggle="dropdown"
                            >
                                {quotation.status?.charAt(0).toUpperCase() + quotation.status?.slice(1)}
                            </button>
                            <ul className="dropdown-menu">
                                {['draft', 'sent', 'accepted', 'rejected', 'expired'].map(status => (
                                    <li key={status}>
                                        <button
                                            className={`dropdown-item ${quotation.status === status ? 'active' : ''}`}
                                            onClick={() => handleStatusChange(status)}
                                        >
                                            {status.charAt(0).toUpperCase() + status.slice(1)}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <Link
                            href={`/queries/${query.id}/quotations/${quotation.id}/edit`}
                            className="btn btn-primary"
                        >
                            <i className="bi bi-pencil me-1"></i>
                            Edit
                        </Link>
                    </div>
                }
            />

            <div className="row g-4">
                {/* Main Content */}
                <div className="col-lg-8">
                    {/* Query & Client Info */}
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6">
                                    <h6 className="text-muted mb-2">Client</h6>
                                    <p className="mb-0 fw-medium">
                                        {query.client?.name || query.guest_name}
                                    </p>
                                    {query.contact_email && (
                                        <small className="text-muted">{query.contact_email}</small>
                                    )}
                                </div>
                                <div className="col-md-6">
                                    <h6 className="text-muted mb-2">Travel Details</h6>
                                    <p className="mb-0">
                                        {quotation.travel_date && (
                                            <span className="fw-medium">
                                                {new Date(quotation.travel_date).toLocaleDateString('en-IN', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric',
                                                })}
                                            </span>
                                        )}
                                        {' • '}
                                        {quotation.nights}N/{(quotation.nights || 0) + 1}D
                                    </p>
                                    <small className="text-muted">
                                        {quotation.adults} Adults
                                        {quotation.children > 0 && `, ${quotation.children} Children`}
                                        {quotation.infants > 0 && `, ${quotation.infants} Infants`}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Itinerary */}
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-transparent border-0">
                            <h5 className="mb-0">
                                <i className="bi bi-calendar-check me-2"></i>
                                Itinerary
                            </h5>
                        </div>
                        <div className="card-body p-0">
                            {days.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-calendar-x display-4 text-muted mb-3 d-block"></i>
                                    <p className="text-muted">No services added yet</p>
                                </div>
                            ) : (
                                <div className="qp-itinerary">
                                    {days.map((day, dayIndex) => (
                                        <motion.div
                                            key={day}
                                            className="qp-day"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: dayIndex * 0.1 }}
                                        >
                                            <div className="qp-day-header">
                                                <div className="qp-day-number">Day {day}</div>
                                                <div className="qp-day-date">
                                                    {quotation.travel_date && (
                                                        new Date(new Date(quotation.travel_date).getTime() + (day - 1) * 24 * 60 * 60 * 1000)
                                                            .toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })
                                                    )}
                                                </div>
                                            </div>
                                            <div className="qp-day-services">
                                                {itemsByDay[day]
                                                    .sort((a, b) => a.sort_order - b.sort_order)
                                                    .map((item, itemIndex) => (
                                                    <motion.div
                                                        key={item.id}
                                                        className="qp-service"
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: dayIndex * 0.1 + itemIndex * 0.05 }}
                                                    >
                                                        <div
                                                            className="qp-service-icon"
                                                            style={{ backgroundColor: getServiceColor(item.service_type) }}
                                                        >
                                                            <i className={`bi ${getServiceIcon(item.service_type)}`}></i>
                                                        </div>
                                                        <div className="qp-service-content">
                                                            <div className="qp-service-header">
                                                                <h6 className="mb-0">{item.name}</h6>
                                                                {item.is_optional && (
                                                                    <span className="badge bg-warning-subtle text-warning ms-2">
                                                                        Optional
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="qp-service-type">
                                                                {serviceTypes[item.service_type]}
                                                                {item.destination && ` • ${item.destination.name}`}
                                                            </div>
                                                            {item.description && (
                                                                <p className="qp-service-desc mb-0">
                                                                    {item.description}
                                                                </p>
                                                            )}
                                                            {item.service_type === 'hotel' && (
                                                                <div className="qp-service-details mt-2">
                                                                    {item.room_type && (
                                                                        <span className="badge bg-primary-subtle text-primary me-1">
                                                                            {item.room_type}
                                                                        </span>
                                                                    )}
                                                                    {item.meal_plan && (
                                                                        <span className="badge bg-success-subtle text-success me-1">
                                                                            {mealPlans[item.meal_plan] || item.meal_plan}
                                                                        </span>
                                                                    )}
                                                                    {item.nights > 0 && (
                                                                        <span className="badge bg-info-subtle text-info">
                                                                            {item.nights} Night{item.nights !== 1 ? 's' : ''}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {item.service_type === 'transfer' && (
                                                                <div className="qp-service-details mt-2 small text-muted">
                                                                    {item.pickup_location && (
                                                                        <span className="me-2">
                                                                            <i className="bi bi-geo-alt me-1"></i>
                                                                            {item.pickup_location}
                                                                        </span>
                                                                    )}
                                                                    {item.dropoff_location && (
                                                                        <span>
                                                                            <i className="bi bi-arrow-right me-1"></i>
                                                                            {item.dropoff_location}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="qp-service-price text-end">
                                                            <div className="fw-bold">{formatCurrency(item.total_selling)}</div>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Inclusions & Exclusions */}
                    {(quotation.inclusions || quotation.exclusions) && (
                        <div className="row g-4 mb-4">
                            {quotation.inclusions && (
                                <div className="col-md-6">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-header bg-success-subtle border-0">
                                            <h6 className="mb-0 text-success">
                                                <i className="bi bi-check-circle me-2"></i>
                                                Inclusions
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                                                {quotation.inclusions}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {quotation.exclusions && (
                                <div className="col-md-6">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-header bg-danger-subtle border-0">
                                            <h6 className="mb-0 text-danger">
                                                <i className="bi bi-x-circle me-2"></i>
                                                Exclusions
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                                                {quotation.exclusions}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Terms */}
                    {(quotation.payment_terms || quotation.cancellation_policy) && (
                        <div className="row g-4">
                            {quotation.payment_terms && (
                                <div className="col-md-6">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-header bg-transparent border-0">
                                            <h6 className="mb-0">
                                                <i className="bi bi-credit-card me-2"></i>
                                                Payment Terms
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                                                {quotation.payment_terms}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {quotation.cancellation_policy && (
                                <div className="col-md-6">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-header bg-transparent border-0">
                                            <h6 className="mb-0">
                                                <i className="bi bi-shield-exclamation me-2"></i>
                                                Cancellation Policy
                                            </h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                                                {quotation.cancellation_policy}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="col-lg-4">
                    {/* Pricing Summary */}
                    <div className="card border-0 shadow-sm mb-4 bg-primary text-white">
                        <div className="card-body">
                            <h5 className="mb-4">Price Summary</h5>

                            <div className="d-flex justify-content-between mb-2">
                                <span className="opacity-75">Total Cost</span>
                                <span className="fw-medium">{formatCurrency(totals.cost)}</span>
                            </div>

                            <div className="d-flex justify-content-between mb-3">
                                <span className="opacity-75">Profit</span>
                                <span className="fw-medium">{formatCurrency(totals.profit)}</span>
                            </div>

                            <hr className="border-white opacity-25" />

                            <div className="d-flex justify-content-between mb-2">
                                <span className="fw-bold fs-5">Total Price</span>
                                <span className="fw-bold fs-5">{formatCurrency(totals.selling)}</span>
                            </div>

                            <div className="text-center mt-3 opacity-75 small">
                                {formatCurrency(totals.perPerson)} per person
                            </div>
                        </div>
                    </div>

                    {/* Quick Info */}
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-body">
                            <div className="mb-3">
                                <div className="small text-muted">Version</div>
                                <div className="fw-medium">{quotation.version}</div>
                            </div>
                            <div className="mb-3">
                                <div className="small text-muted">Created By</div>
                                <div className="fw-medium">{quotation.created_by?.name || 'Unknown'}</div>
                            </div>
                            <div className="mb-3">
                                <div className="small text-muted">Created On</div>
                                <div className="fw-medium">
                                    {new Date(quotation.created_at).toLocaleDateString('en-IN', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                    })}
                                </div>
                            </div>
                            {quotation.sent_at && (
                                <div className="mb-3">
                                    <div className="small text-muted">Sent On</div>
                                    <div className="fw-medium">
                                        {new Date(quotation.sent_at).toLocaleDateString('en-IN', {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric',
                                        })}
                                    </div>
                                </div>
                            )}
                            {quotation.accepted_at && (
                                <div>
                                    <div className="small text-muted">Accepted On</div>
                                    <div className="fw-medium text-success">
                                        {new Date(quotation.accepted_at).toLocaleDateString('en-IN', {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric',
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="d-grid gap-2">
                        <Link
                            href={`/queries/${query.id}/quotations/${quotation.id}/edit`}
                            className="btn btn-primary"
                        >
                            <i className="bi bi-pencil me-1"></i>
                            Edit Quotation
                        </Link>
                        <button className="btn btn-outline-primary" disabled>
                            <i className="bi bi-printer me-1"></i>
                            Print/PDF
                        </button>
                        <button className="btn btn-outline-primary" disabled>
                            <i className="bi bi-envelope me-1"></i>
                            Email to Client
                        </button>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
